import { DateTime } from 'luxon'

import { generateRedirectUrl } from '@domain-group/javascript-utils'

import type {
  CampaignModel,
  CampaignQuoteTemplateItemModel,
  OrderItemModel,
  OrderModel,
  PublicationEditionModel,
  PublicationModel,
  ServiceModel,
  ServiceProductModel,
  StatusKey,
  StatusModel,
} from '@models/types'

import type { EntitiesState } from '@redux/entities'

import { pendingStatusKeys } from '@functions/status'

import type { SelectedOption } from '@functions/selectPriceListItemOptionValue'

import { generateUrls } from '@hooks/useOrder'

type BuildOrderItemUrlParams = {
  order: OrderModel,
  orderItem: OrderItemModel,
  status: StatusModel,
}

export const buildOrderItemUrl = (params: BuildOrderItemUrlParams) => {
  const { order, orderItem, status } = params

  const { approvedCampaignOrdersUrl, pendingCampaignOrdersUrl } = generateUrls(
    order,
    { prefixRoute: `campaigns/${order?.campaign_id}/edit` },
  )

  const orderUrl = status?.key === 'approved' ? approvedCampaignOrdersUrl : pendingCampaignOrdersUrl

  const payload = encodeURIComponent(JSON.stringify({ orderItem: { id: orderItem.id } }))

  return `${orderUrl}?redirect=${generateRedirectUrl()}&showModal=ManageOrderItemModal&showModalPayload=${payload}`
}

type BuildFullDescriptionParams = {
  orderItem: OrderItemModel,
  publication: PublicationModel,
  publicationEdition: PublicationEditionModel,
}

export const buildFullDescription = (params: BuildFullDescriptionParams) => {
  const { orderItem, publication, publicationEdition } = params

  let title = `${orderItem.title}`

  if (publication && publication.title){
    title = `${publication.title} - ${title}`
  }

  if (publicationEdition && publicationEdition.publication_date){
    title = `${title} - ${publicationEdition.publication_date}`
  }

  return title
}

type BuildItemTitleParams = {
  excludeOptionsString?: boolean,
  orderableItem: CampaignQuoteTemplateItemModel | OrderItemModel | {},
}

export const buildItemTitle = (params: BuildItemTitleParams) => {
  const {
    excludeOptionsString = false,
    orderableItem,
  } = params

  // @ts-expect-error TS2339
  if (orderableItem.title){
    // @ts-expect-error TS2339
    const { title, options_string, quantity } = orderableItem

    const base = quantity === 1 ? title : `${quantity} x ${title}`
    const optionsString = !excludeOptionsString && options_string ? options_string : ''

    return `${base}${optionsString}`
  }

  return ''
}

type CreateItemArtworkUrlParams = {
  campaign: CampaignModel,
  orderItem: OrderItemModel,
  options: {
    newVersion?: boolean,
    redirectUrl?: string,
  },
}

export function createItemArtworkUrl(params: CreateItemArtworkUrlParams) {
  const { orderItem, campaign, options = {} } = params

  const { newVersion, redirectUrl } = options || {}
  const { id: campaignId } = campaign || {}
  const { id } = orderItem || {}

  const backLink = `?redirect=${redirectUrl || generateRedirectUrl()}`

  if (newVersion){
    return `#/campaigns/${campaignId}/artwork_builder${backLink}#/new?orderItemId=${id}`
  }

  return `#/artworks/builder${backLink}#/orderItems/${id}/new-artwork`
}

type GetGridSizeParams = {
  serviceProduct: ServiceProductModel,
}

export function getGridSize(params: GetGridSizeParams) {
  const { serviceProduct } = params
  return serviceProduct?.options?.publication?.grid || {}
}

type GetPublicationItemStatusStringParams = {
  artworkApproved: boolean,
  isBeforeBookingDeadline: boolean,
  orderStatusKey: StatusKey,
}

export function getPublicationItemStatusString(params: GetPublicationItemStatusStringParams) {
  const { artworkApproved, isBeforeBookingDeadline, orderStatusKey } = params

  if (orderStatusKey === 'pending'){
    if (artworkApproved) return 'Artwork Approved'
    if (!isBeforeBookingDeadline) return 'Booking Deadline Passed'
    return 'Booking Pending'
  }

  if (orderStatusKey === 'booked'){
    return 'Booked with Publication'
  }

  if (orderStatusKey === 'approved'){
    return 'Released to Publication'
  }

  return 'Unknown Status'
}

type ItemRequiresArtworkParams = {
  service: ServiceModel | {},
  serviceProduct: ServiceProductModel | {},
}

export function itemRequiresArtwork(params: ItemRequiresArtworkParams) {
  const { serviceProduct, service } = params

  // @ts-expect-error TS2339
  if (service?.options?.templatable && !service.options?.digital){
    return true
  }

  // @ts-expect-error TS2339
  if (serviceProduct?.options?.digital?.templatable){
    return true
  }

  // @ts-expect-error TS2339
  if (serviceProduct?.options?.proposal?.templatable){
    return true
  }

  return false
}

type ItemRequiresAttachmentParams = {
  serviceProduct: ServiceProductModel | {},
}

export function itemRequiresAttachment(params: ItemRequiresAttachmentParams) {
  const { serviceProduct } = params
  // @ts-expect-error TS2339
  return serviceProduct?.options?.attachable?.required || false
}

type ItemRequiresExternalManagementParams = {
  orderItem: OrderItemModel | {},
}

export function itemRequiresExternalManagement(params: ItemRequiresExternalManagementParams) {
  const { orderItem } = params
  // @ts-expect-error TS2339
  return !!orderItem.deep_link_url || false
}

type ItemRequiresNoteParams = {
  service: ServiceModel | {},
}

export function itemRequiresNote(params: ItemRequiresNoteParams) {
  const { service } = params
  // @ts-expect-error TS2339
  return service.options?.notable || false
}

type ItemRequiresTaskParams = {
  service: ServiceModel | {},
}

export function itemRequiresTask(params: ItemRequiresTaskParams) {
  const { service } = params
  // @ts-expect-error TS2339
  return service?.options?.taskable || false
}

export const buildOptionsString = (options: SelectedOption[] | undefined) => {
  if (!options) return 'No Options'

  const optionValues = options.map(option => (option.value ? option.value.title : ''))
  const optionString = optionValues.reduce((acc, string) => `${acc}/${string}`, '')
  return optionString || 'No Options'
}

export default buildOptionsString

type ValidateOrderItemParams = {
  artworkIdsRequireDownload?: number[],
  entities: {
    artworkPages: EntitiesState['artworkPages'],
    artworks: EntitiesState['artworks'],
    orderItemTasks: EntitiesState['orderItemTasks'],
    serviceProducts: EntitiesState['serviceProducts'],
    services: EntitiesState['services'],
  },
  orderItem: OrderItemModel,
  requireArtworkDataSync?: boolean,
}

type ValidateOrderItemErrorType = 'artwork-missing'
 | 'artwork-data-changes'
 | 'artwork-warning'
 | 'artwork-page-count'
 | 'artwork-template-unused'
 | 'task-address-missing'
 | 'task-instructions-missing'
 | 'task-date-missing'
 | 'task-date-past'

export type ValidateOrderItemError = {
  blocker: boolean,
  filterType: string,
  message: string,
  type: ValidateOrderItemErrorType,
}

export function validateOrderItem(params: ValidateOrderItemParams): ValidateOrderItemError[] {
  const {
    artworkIdsRequireDownload,
    entities: {
      artworkPages,
      artworks,
      orderItemTasks,
      serviceProducts,
      services,
    },
    orderItem,
    requireArtworkDataSync,
  } = params

  const { artwork_id, service_product_id, service_id } = orderItem

  const service = service_id && services[service_id] ? services[service_id] : {}
  const serviceProduct = service_product_id && serviceProducts[service_product_id]
    ? serviceProducts[service_product_id] : {}

  const errors: ValidateOrderItemError[] = []

  // Artwork
  if (itemRequiresArtwork({ service, serviceProduct })){
    // @ts-expect-error TS2538
    const artwork = artworks[artwork_id] || {}
    const pageArray = Object.values(artworkPages).filter(page => page.artwork_id === artwork.id)

    if (!artwork.id){
      errors.push({
        blocker: true,
        filterType: 'artwork',
        message: 'Artwork is required for this item.',
        type: 'artwork-missing',
      })
    }

    // Check if data has changed
    if (artwork && artwork.data_changes && artwork.data_changes.length){
      errors.push({
        blocker: (typeof requireArtworkDataSync === 'undefined' || requireArtworkDataSync),
        filterType: 'artwork',
        message: 'Artwork data has changed.',
        type: 'artwork-data-changes',
      })
    }

    // Check if any pages have warnings
    if (artwork.id && pageArray){
      pageArray.some((artworkPage) => {
        // @ts-expect-error TS2339
        if (artworkPage.warning_count && artworkPage.warning_count > 0){
          errors.push({
            blocker: false,
            filterType: 'artwork',
            message: 'Artwork has warnings. Please double check.',
            type: 'artwork-warning',
          })
          return true
        }
        return false
      })
    }

    // Check Page Count
    // @ts-expect-error TS2339
    if (artwork.id && serviceProduct?.options?.artwork){
      // @ts-expect-error TS2339
      const { options: { artwork: { min_pages, max_pages } } } = serviceProduct

      const pageCount = pageArray ? pageArray.length : 0
      if (pageCount < min_pages || pageCount > max_pages){
        const message = `Artwork has too ${pageCount > max_pages ? 'many' : 'few'} pages.`
        errors.push({
          blocker: true,
          filterType: 'artwork',
          message,
          type: 'artwork-page-count',
        })
      }
    }

    // Check if first use of template
    if (artwork.id && artworkIdsRequireDownload && artworkIdsRequireDownload.includes(artwork.id)){
      errors.push({
        blocker: true,
        filterType: 'artwork',
        message: 'First use of template. Please download.',
        type: 'artwork-template-unused',
      })
    }
  }

  // Task
  // @ts-expect-error TS2339
  if (itemRequiresTask({ service }) && orderItem.tasks){
    // @ts-expect-error TS2339
    orderItem.tasks.forEach((taskId: number) => {
      const task = orderItemTasks[taskId] || {}
      if (!task.address || !task.address.length){
        errors.push({
          blocker: true,
          filterType: 'task',
          message: 'Task Address is required.',
          type: 'task-address-missing',
        })
      }

      if (!task.instructions){
        errors.push({
          blocker: true,
          filterType: 'task',
          message: 'Task Instructions are required.',
          type: 'task-instructions-missing',
        })
      }

      if (!task.due_date){
        errors.push({
          blocker: true,
          filterType: 'task',
          message: 'Task Date is required.',
          type: 'task-date-missing',
        })
      }

      if (task.due_date && (DateTime.fromFormat(task.due_date, 'dd/MM/yyyy') < DateTime.now())){
        errors.push({
          blocker: true,
          filterType: 'task',
          message: 'Task Date is in the past.',
          type: 'task-date-past',
        })
      }
    })
  }

  return errors
}

interface FilterPendingOrderItemsParams {
  orderItems: OrderItemModel[],
  services: EntitiesState['services'],
  serviceProducts: EntitiesState['serviceProducts'],
  statuses: EntitiesState['statuses'],
}

export const filterPendingOrderItems = (params: FilterPendingOrderItemsParams) => {
  const {
    orderItems, services, serviceProducts, statuses,
  } = params

  const filtered = Object.values(orderItems).filter((orderItem) => {
    const status = statuses[orderItem.status_id] || {}
    // @ts-expect-error TS2538
    const service = services[orderItem.service_id] || {}
    // @ts-expect-error TS2538
    const serviceProduct = serviceProducts[orderItem.service_product_id] || {}

    // @ts-expect-error TS2345
    const isPending = pendingStatusKeys.includes(status.key)

    return isPending && itemRequiresArtwork({ service, serviceProduct })
  })

  return filtered
}
